import {
  TGetTransferPricingIntercompanyTransactionsResponse,
  TTransactionListFilters,
} from '@commenda/types';
import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';

export type TTransactionListQueryOptions = {
  filters?: TTransactionListFilters;
  includeFilterFacets?: boolean;
};

export function useTransferPricingTransactions(
  companyId: number,
  serviceProviderEntityIds?: number[],
  listQuery?: TTransactionListQueryOptions
) {
  const queryString = new URLSearchParams();

  if (companyId) {
    queryString.append('companyId', companyId.toString());
  }

  if (serviceProviderEntityIds?.length) {
    queryString.append(
      'serviceProviderEntityIds',
      serviceProviderEntityIds.join(',')
    );
  }

  if (listQuery?.filters?.dateStart)
    queryString.set('filterConfig[dateStart]', listQuery.filters.dateStart);
  if (listQuery?.filters?.dateEnd)
    queryString.set('filterConfig[dateEnd]', listQuery.filters.dateEnd);
  if (listQuery?.filters?.amountMin !== undefined)
    queryString.set(
      'filterConfig[amountMin]',
      String(listQuery.filters.amountMin)
    );
  if (listQuery?.filters?.amountMax !== undefined)
    queryString.set(
      'filterConfig[amountMax]',
      String(listQuery.filters.amountMax)
    );

  listQuery?.filters?.transactionTypes?.forEach((transactionType) =>
    queryString.append('filterConfig[transactionTypes][]', transactionType)
  );
  listQuery?.filters?.methods?.forEach((method) =>
    queryString.append('filterConfig[methods][]', method)
  );
  listQuery?.filters?.policyIds?.forEach((policyId) =>
    queryString.append('filterConfig[policyIds][]', policyId)
  );
  listQuery?.filters?.testedPartyEntityIds?.forEach((testedPartyEntityId) =>
    queryString.append(
      'filterConfig[testedPartyEntityIds][]',
      String(testedPartyEntityId)
    )
  );

  if (listQuery?.includeFilterFacets)
    queryString.set('includeFilterFacets', 'true');

  const URL = companyId
    ? `transfer-pricing/transactions?${queryString.toString()}`
    : null;

  const {
    data,
    error,
    mutate,
    isLoading,
    isValidating,
  }: {
    data?: TGetTransferPricingIntercompanyTransactionsResponse;
    error?: unknown;
    mutate: KeyedMutator<unknown>;
    isLoading: boolean;
    isValidating: boolean;
  } = useSWR(
    URL,
    SWRFetcher({
      method: 'GET',
    }),
    {
      keepPreviousData: Boolean(listQuery),
    }
  );

  return {
    data: data?.transactions,
    count: data?.count,
    meta: data?.meta,
    facets: data?.facets,
    error,
    mutate,
    isLoading,
    isValidating,
  };
}
